<div class="w-full max-w-full p-4 lg:p-0">

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex items-center justify-center p-12">
    <mat-spinner diameter="50"></mat-spinner>
  </div>

  <!-- User Content -->
  <div *ngIf="!isLoading && user">
    
    <!-- User Header Card -->
    <div class="bg-card rounded-2xl shadow-lg overflow-hidden mb-6">
      <!-- Header Background -->
      <div class="h-32 bg-gradient-to-r from-primary-500 to-primary-700"></div>
      
      <!-- User Info -->
      <div class="px-6 pb-6">
        <!-- Avatar -->
        <div class="flex items-start gap-4 -mt-16 mb-4">
          <div class="flex items-center justify-center w-32 h-32 rounded-full bg-white dark:bg-gray-800 border-4 border-white dark:border-gray-800 shadow-lg overflow-hidden">
            <div class="flex items-center justify-center w-full h-full rounded-full bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200">
              <!-- Logo firmy lub inicjały -->
              <img 
                *ngIf="user.company?.logoUrl" 
                [src]="user.company.logoUrl" 
                [alt]="user.company?.name || 'Logo'"
                class="w-full h-full object-cover rounded-full"
                (error)="$event.target.style.display='none'">
              <span *ngIf="!user.company?.logoUrl" class="text-4xl font-bold">
                {{ getInitials() }}
              </span>
            </div>
          </div>
          
          <!-- Actions -->
          <div class="ml-auto mt-20">
            <button 
              mat-raised-button 
              [color]="isEditable ? 'warn' : 'primary'"
              (click)="toggleEditMode()"
              class="shadow-sm">
              <mat-icon [svgIcon]="isEditable ? 'tabler_outline:lock-open' : 'tabler_outline:pencil'"></mat-icon>
              <span class="ml-2">{{ isEditable ? 'Wyłącz edycję' : 'Włącz edycję' }}</span>
            </button>
            <button 
              mat-stroked-button 
              color="primary"
              [routerLink]="'/admin/users'"
              class="ml-2"
              matTooltip="Powrót do listy użytkowników">
              <mat-icon [svgIcon]="'tabler_outline:arrow-left'"></mat-icon>
              <span class="ml-2">Powrót</span>
            </button>
          </div>
        </div>
        
        <!-- User Details -->
        <div class="space-y-2">
          <h1 class="text-3xl font-bold">
            {{ user.firstName }} {{ user.lastName }}
          </h1>
          
          <div class="flex items-center gap-4 text-gray-600 dark:text-gray-300">
            <div class="flex items-center gap-2">
              <mat-icon class="text-lg" [svgIcon]="'tabler_outline:mail'"></mat-icon>
              <span>{{ user.emailAddress }}</span>
            </div>
            <div *ngIf="user.phoneNumber" class="flex items-center gap-2">
              <mat-icon class="text-lg" [svgIcon]="'tabler_outline:phone'"></mat-icon>
              <span>{{ user.phoneNumber }}</span>
            </div>
          </div>

          <!-- Statystyki -->
          <div class="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <mat-icon class="text-blue-600 dark:text-blue-400" [svgIcon]="'tabler_outline:file-invoice'"></mat-icon>
              <div class="flex flex-col">
                <span class="text-xs text-gray-500 dark:text-gray-400">Dokumenty</span>
                <span class="text-lg font-bold text-blue-600 dark:text-blue-400">{{ user.stats?.documentsCreated || 0 }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <mat-icon class="text-green-600 dark:text-green-400" [svgIcon]="'tabler_outline:users'"></mat-icon>
              <div class="flex flex-col">
                <span class="text-xs text-gray-500 dark:text-gray-400">Klienci</span>
                <span class="text-lg font-bold text-green-600 dark:text-green-400">{{ user.stats?.clientsCreated || 0 }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <mat-icon class="text-purple-600 dark:text-purple-400" [svgIcon]="'tabler_outline:package'"></mat-icon>
              <div class="flex flex-col">
                <span class="text-xs text-gray-500 dark:text-gray-400">Produkty</span>
                <span class="text-lg font-bold text-purple-600 dark:text-purple-400">{{ user.stats?.productsCreated || 0 }}</span>
              </div>
            </div>
          </div>
          
          <!-- Informacje o aktywności -->
          <div class="flex flex-wrap items-center gap-3 mt-4">
            <div 
              class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
              [matTooltip]="'Utworzono: ' + formatDate(user.createdAt)"
              matTooltipPosition="below">
              <mat-icon class="text-gray-600 dark:text-gray-400 text-base" [svgIcon]="'tabler_outline:calendar-plus'"></mat-icon>
              <div class="flex flex-col">
                <span class="text-xs text-gray-500 dark:text-gray-400">Konto utworzone</span>
                <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ getAccountAge() }} temu</span>
              </div>
            </div>
            
            <div 
              *ngIf="user.lastLoginAt"
              class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
              [matTooltip]="'Dokładna data: ' + formatDate(user.lastLoginAt)"
              matTooltipPosition="below">
              <mat-icon class="text-gray-600 dark:text-gray-400 text-base" [svgIcon]="'tabler_outline:login'"></mat-icon>
              <div class="flex flex-col">
                <span class="text-xs text-gray-500 dark:text-gray-400">Ostatnie logowanie</span>
                <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ getRelativeTime(user.lastLoginAt) }}</span>
              </div>
            </div>
            
            <div 
              *ngIf="user.lastSyncAt"
              class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
              [matTooltip]="'Dokładna data: ' + formatDate(user.lastSyncAt)"
              matTooltipPosition="below">
              <mat-icon class="text-gray-600 dark:text-gray-400 text-base" [svgIcon]="'tabler_outline:refresh'"></mat-icon>
              <div class="flex flex-col">
                <span class="text-xs text-gray-500 dark:text-gray-400">Ostatnia synchronizacja</span>
                <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ getRelativeTime(user.lastSyncAt) }}</span>
              </div>
            </div>

            <div 
              *ngIf="!user.lastLoginAt"
              class="flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <mat-icon class="text-orange-600 dark:text-orange-400 text-base" [svgIcon]="'tabler_outline:alert-circle'"></mat-icon>
              <span class="text-sm font-medium text-orange-700 dark:text-orange-300">Nigdy się nie zalogował</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Content with Vertical Navigation -->
    <div class="bg-card rounded-2xl shadow-lg overflow-hidden">
      <div class="flex flex-col md:flex-row">
        
        <!-- Vertical Navigation -->
        <div class="w-full md:w-64 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div class="flex flex-col p-2">
            <a 
              *ngFor="let tab of tabs"
              [routerLink]="['/admin/users', userId, tab.route]"
              class="flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all cursor-pointer"
              [ngClass]="{
                'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium': selectedTab === tab.id,
                'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800': selectedTab !== tab.id
              }">
              <mat-icon [svgIcon]="tab.icon"></mat-icon>
              <span>{{ tab.label }}</span>
            </a>
          </div>
        </div>

        <!-- Content Area -->
        <div class="flex-1 min-w-0">
          <router-outlet></router-outlet>
        </div>
      </div>
    </div>

  </div>

</div>